import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '@accounts/config';
import { Confirmation, Credentials, Second } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Provider, Refusal } from '@accounts/transport';
import { Button, Callout, Field, Form, Loading } from '@beyond-js/ui/react';
import { Problem, useCountdown, Wordmark } from '@accounts/ui';
import { Carried } from './signin/carried';

/**
 * Where someone lands from a message that proves an address: finishing a sign-up, or confirming a new
 * sign-in address (ACC-10).
 *
 * A message written by Accounts carries a token hash (`?token_hash=…&type=…&next=…`), which the service
 * redeems; an older one returns a provider token in the address, exchanged as an ordinary sign-in. Either
 * way the person is then signed in and continues where the sign-in was going (`next`, `Confirmation`).
 *
 * A link that was used or expired says so and offers **Send the link again** right here, with the wait
 * shown (ACC-01): a lost or expired message no longer locks anyone out. It does not say whether the
 * address is registered. While the address is empty the action waits quietly and a press puts the caret in
 * it (D183); what is written and is not an address is said there.
 */
export function Confirm() {
	const { text, client, accounts, refresh, language } = usePresence();
	const credentials = useMemo(() => new Credentials(client), [client]);
	const navigate = useNavigate();
	const [parameters] = useSearchParams();
	const carried = useMemo(() => new Carried(parameters, globalThis.location.origin).parameters, [parameters]);
	const confirmation = useMemo(() => new Confirmation(carried, config.authority), [carried]);

	const [working, setWorking] = useState(true);
	const [expired, setExpired] = useState(false);
	const [failure, setFailure] = useState<unknown>();
	const [email, setEmail] = useState('');
	const [sent, setSent] = useState(false);
	const [busy, setBusy] = useState(false);
	const [invalid, setInvalid] = useState<string>();
	const [waiting, wait] = useCountdown();

	useEffect(() => {
		const hash = parameters.get('token_hash');
		const type = parameters.get('type') ?? 'signup';
		const returned = hash ? undefined : Provider.returned();
		if (!hash && (returned?.error || (!returned?.token && !returned?.code))) {
			setExpired(true);
			setWorking(false);
			return;
		}
		const entering = hash
			? credentials.confirm(type, hash)
			: (returned?.token ? Promise.resolve(returned.token) : accounts.exchange(returned?.code as string)).then(token => accounts.enter(token));
		entering
			.then(answer => {
				if (answer.factor) {
					Second.keep(answer.factor);
					navigate(`/signin/factor?${carried}`);
					return;
				}
				// Replaced, so Back does not reach a link that has been spent
				const next = confirmation.handoff;
				if (next) globalThis.location.replace(next);
				else if (type === 'email_change') globalThis.location.replace('/security?email=confirmed');
				else return refresh();
			})
			.catch(error => (Refusal.is(error) && error.unreachable ? setFailure(error) : setExpired(true)))
			.finally(() => setWorking(false));
		// Read once per page load: the link is spent
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Something is written here: an empty field stopped the submission in `Form`, saying nothing
	async function resend() {
		const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
		setInvalid(valid ? undefined : text('error.field.email'));
		if (!valid) return;
		setBusy(true);
		setFailure(undefined);
		try {
			const answer = await credentials.resend(email, confirmation.address(globalThis.location.origin));
			setSent(true);
			wait(answer.retry);
		} catch (error) {
			setFailure(error);
			if (Refusal.is(error) && error.retry) wait(error.retry);
		} finally {
			setBusy(false);
		}
	}

	// While the link is redeemed the screen waits under the opening as its `signin` step: a confirmation that
	// works leaves at once, so the opening hands over only to what follows or to a link that no longer works
	return (
		<div data-bui-opening-wait={working ? 'signin' : undefined} className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
			<header className="flex flex-col gap-3">
				<Wordmark label="Accounts" />
				<div>
					<h1 className="text-title-1 text-ink">{text('confirm.title')}</h1>
					{working ? <p className="mt-1 text-body text-ink-muted">{text('confirm.lead')}</p> : null}
				</div>
			</header>

			{working ? <Loading label={Loading.labels[language]} /> : null}
			{failure ? <Problem error={failure} /> : null}
			{expired ? (
				<>
					<Callout tone="warning" live title={text('confirm.expired')} body={text('confirm.resend.lead')} />
					{sent ? <Callout tone="success" live title={text('signin.resend.sent', { email })} body={waiting > 0 ? text('signin.resend.wait', { count: waiting }) : undefined} /> : null}
					<Form
						className="flex flex-col gap-4"
						onSubmit={event => {
							event.preventDefault();
							void resend();
						}}
					>
						<Field label={text('signin.email')} error={invalid}>
							<input type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} />
						</Field>
						<Button type="submit" variant="primary" busy={busy} disabled={waiting > 0}>
							{text('signin.resend')}
						</Button>
						<a className="text-center text-body text-ink-muted underline" href={confirmation.signin}>
							{text('confirm.again')}
						</a>
					</Form>
				</>
			) : null}
		</div>
	);
}
